import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Loader2, CheckCircle2, Copy, Users, Check } from 'lucide-react';
import { toast } from 'sonner';

function AffiliationStatus({ affiliation }) {
  const { data: referrerInfo, isLoading } = useQuery({
    queryKey: ['publicUserInfo', affiliation.referrer_user_id],
    queryFn: async () => {
      try {
        const { data: userInfo } = await supabase.from('users').select('*').eq('id', affiliation.referrer_user_id).single();
        return userInfo;
      } catch (error) {
        console.warn('Failed to fetch referrer info:', error);
        return null;
      }
    }
  });

  const displayUsername = referrerInfo?.username || affiliation.referrer_username || 'Unknown';

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-green-500" />
        <h4 className="text-sm font-semibold text-white">Affilié à</h4>
      </div>
      <div className="bg-[#1B1E28] border border-green-500/20 rounded-lg px-4 py-3">
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-[#6E2BFF]" />
        ) : (
          <p className="text-lg font-bold text-white">@{displayUsername}</p>
        )}
      </div>
      <p className="text-xs text-[#9CA0AB]">
        Affiliation enregistrée le {new Date(affiliation.created_at).toLocaleDateString('fr-FR')}
      </p>
    </div>
  );
}

const DEFAULT_REFERRAL_LIMITS = { 1: 1, 3: 2, 5: 3, 7: 4, 8: 5 };

function computeReferralLimit(level) {
  let result = 0;
  for (const [threshold, slots] of Object.entries(DEFAULT_REFERRAL_LIMITS)) {
    if (level >= Number(threshold) && slots > result) result = slots;
  }
  return result || 1;
}

export default function AffiliationSection({ user }) {
  const [referrerUsername, setReferrerUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  // Referral slots (current user as sponsor)
  const { data: referralStats } = useQuery({
    queryKey: ['referralStats', user?.id, user?.level],
    queryFn: async () => {
      const affiliations = await base44.entities.Affiliation.filter({
        referrer_user_id: user.id
      });
      const used = affiliations?.length || 0;
      const limit = computeReferralLimit(user.level || 1);
      return { used, limit, remaining: Math.max(0, limit - used) };
    },
    enabled: !!user?.id
  });

  // Vérifier si l'utilisateur a déjà une affiliation
  const { data: affiliation, isLoading } = useQuery({
    queryKey: ['myAffiliation', user?.id],
    queryFn: async () => {
      const affiliations = await base44.entities.Affiliation.filter({
        affiliate_user_id: user.id
      });
      
      if (!affiliations || affiliations.length === 0) return null;
      
      return affiliations[0];
    },
    enabled: !!user?.id,
    staleTime: 0,
    refetchOnMount: 'stale'
  });

  const createAffiliationMutation = useMutation({
    mutationFn: async (referrerUsername) => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { data: referrer } = await supabase.from('users').select('id').eq('username', referrerUsername.toLowerCase().trim()).single();
      const { data: affiliation, error } = await supabase.from('affiliations').insert({
        affiliate_user_id: authUser.id,
        referrer_user_id: referrer?.id,
        referrer_username: referrerUsername.toLowerCase().trim()
      }).select().single();
      if (error) throw error;
      const response = { data: affiliation };
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['myAffiliation', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['userProgress', user?.id] });
      // Petit délai pour s'assurer que les données sont synchro
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ['myAffiliation', user?.id] });
        queryClient.refetchQueries({ queryKey: ['userProgress', user?.id] });
      }, 500);
      toast.success(`Affiliation enregistrée ! +${data.rewards.xp} XP${data.rewards.tickets > 0 ? ` et +${data.rewards.tickets} ticket` : ''}`);
      setReferrerUsername('');
      setError('');
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.error || error.message || 'Erreur lors de l\'affiliation';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!referrerUsername.trim()) {
      setError('Veuillez entrer un pseudo');
      return;
    }
    setError('');
    setIsSubmitting(true);
    await createAffiliationMutation.mutateAsync(referrerUsername);
    setIsSubmitting(false);
  };

  const handleCopyUsername = () => {
    navigator.clipboard.writeText(user.username);
    toast.success('Pseudo copié !');
    setCopied(true);
  };

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);



  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-[#6E2BFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mon pseudo (code à partager) */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#6E2BFF]" />
          <h4 className="text-sm font-semibold text-white">Mon pseudo</h4>
        </div>
        
        <div className="flex gap-2">
          <div className="flex-1 bg-[#1B1E28] border border-[#6E2BFF] rounded-lg px-3 py-2 flex items-center">
            <p className="text-base font-bold text-[#6E2BFF]">@{user.username}</p>
          </div>
          <Button
            onClick={handleCopyUsername}
            variant="outline"
            className="border-[#6E2BFF] text-[#6E2BFF] hover:bg-[#6E2BFF] hover:text-white transition-all"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>

        <p className="text-xs text-[#9CA0AB]">
          Partage ton pseudo. Chaque affiliation = +10 XP pour vous deux.
          <br />
          +1 ticket chacun seulement si un challenge affiliation est en cours.
        </p>

        {/* Referral slots */}
        {referralStats && (
          <div className="bg-[#1B1E28] border border-[#2A2D3A] rounded-lg px-4 py-3 mt-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#9CA0AB]">Places d'invitation</p>
              <p className="text-sm font-bold text-white">
                {referralStats.remaining} <span className="text-[#9CA0AB] font-normal">/ {referralStats.limit}</span>
              </p>
            </div>
            <div className="w-full h-2 bg-[#0B0D12] rounded-full mt-2 overflow-hidden">
              <div
                className="h-full bg-[#6E2BFF] rounded-full transition-all"
                style={{ width: `${referralStats.limit > 0 ? (referralStats.used / referralStats.limit) * 100 : 0}%` }}
              />
            </div>
            <p className="text-[10px] text-[#9CA0AB] mt-1">
              {referralStats.used} utilisée{referralStats.used !== 1 ? 's' : ''} sur {referralStats.limit}
            </p>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-[#1B1E28]"></div>

      {/* Affiliation status */}
      {affiliation ? (
        <AffiliationStatus affiliation={affiliation} />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#6E2BFF]" />
            <h4 className="text-sm font-semibold text-white">Tu as un affiliateur ?</h4>
          </div>
          
          <Input
            type="text"
            placeholder="Pseudo de ton affiliateur"
            value={referrerUsername}
            onChange={(e) => setReferrerUsername(e.target.value)}
            className="bg-[#1B1E28] border-[#6E2BFF] text-white"
            maxLength={20}
            disabled={isSubmitting}
          />

          {error && (
            <p className="text-sm text-red-400">{error}</p>
          )}

          <Button
            type="submit"
            disabled={isSubmitting || !referrerUsername.trim()}
            className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Valider
          </Button>

          <p className="text-xs text-[#9CA0AB]">
            Tu gagnes 10 XP et ton affiliateur aussi.
            <br />
            +1 ticket chacun seulement si un challenge affiliation est en cours.
          </p>
        </form>
      )}
    </div>
  );
}